/**
 * <DuelInbox> — /study/duels
 *
 * Buddy Duel inbox. Four sections:
 *   - Incoming invitations (Accept / Decline)
 *   - Active duels (Continue playing)
 *   - Outgoing pending (waiting for a partner — with Cancel)
 *   - Completed (last 20 with final score)
 *
 * Auto-refreshes every 8s while the tab is visible.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STUDY_THEME, paperBackground } from '../components/study/studyTheme';
import {
  getDuelInbox, acceptDuel, declineDuel, cancelDuel,
} from '../lib/studyApi';

const cardStyle = {
  background: 'white', borderRadius: 12, padding: 20,
  boxShadow: STUDY_THEME.shadow, marginBottom: 12,
  border: `1px solid ${STUDY_THEME.line}`,
};

const sectionLabel = {
  fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase',
  fontWeight: 700, color: STUDY_THEME.goldDeep,
  marginBottom: 12, marginTop: 32,
};

const pillBtn = (bg, fg, border) => ({
  padding: '8px 16px', fontSize: 13, fontWeight: 700,
  background: bg, color: fg,
  border: `1px solid ${border || bg}`, borderRadius: 8,
  cursor: 'pointer', fontFamily: 'inherit',
});

function ScoreLine({ me, opp, myLabel = 'You', oppLabel = 'Partner' }) {
  return (
    <div style={{ display: 'flex', gap: 20, fontSize: 13 }}>
      <span><b>{myLabel}:</b> <span style={{
        color: me >= opp ? '#16A34A' : STUDY_THEME.muted, fontWeight: 700,
      }}>{me}</span></span>
      <span><b>{oppLabel}:</b> <span style={{
        color: opp > me ? '#DC2626' : STUDY_THEME.muted, fontWeight: 700,
      }}>{opp}</span></span>
    </div>
  );
}

export default function DuelInbox() {
  const navigate = useNavigate();
  const [inbox, setInbox] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');   // duel_id currently mutating
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await getDuelInbox();
      setInbox(d);
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load inbox');
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 15000);   // safety net
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [load]);

  // Real-time WebSocket — reconnects on close, refetches inbox on any event.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsUrl = base.replace(/^http/, 'ws') + `/api/study/duels/ws?token=${encodeURIComponent(token)}`;
    let alive = true;
    let ws;
    let retryTimer;
    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        ws.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data || '{}');
            if (['invited', 'accepted', 'declined', 'cancelled', 'answered', 'complete'].includes(data.type)) {
              load();
            }
          } catch { /* ignore */ }
        };
        ws.onclose = () => {
          if (!alive) return;
          // Auto-reconnect with backoff.
          retryTimer = setTimeout(connect, 3000);
        };
      } catch { /* ignore */ }
    };
    connect();
    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, [load]);

  const act = async (fn, duelId, thenGo) => {
    setBusy(duelId);
    try {
      await fn(duelId);
      if (thenGo) navigate(thenGo);
      else await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Action failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <div style={paperBackground()} data-testid="duel-inbox-page">
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ ...sectionLabel, marginTop: 0 }}>Institute of Analytics · Active Recall</div>
            <h1 style={{
              fontFamily: '"Playfair Display", serif', fontSize: 40,
              margin: '2px 0 4px', fontWeight: 700, color: STUDY_THEME.ink,
            }}>
              🥊 Buddy Duels
            </h1>
            <p style={{ color: STUDY_THEME.inkSoft, margin: 0, fontSize: 15 }}>
              Challenge a colleague on any deck. Play together or async — same cards, same order.
            </p>
          </div>
          <button
            onClick={() => navigate('/study')}
            data-testid="inbox-back-hub"
            style={pillBtn('white', STUDY_THEME.inkSoft, STUDY_THEME.line)}
          >← Deck Hub</button>
        </div>

        {err && (
          <div data-testid="inbox-error" style={{
            padding: 14, borderRadius: 10, background: '#FEE2E2',
            color: '#991B1B', marginBottom: 20,
          }}>{err}</div>
        )}

        {!inbox ? (
          <div style={{ padding: 40, textAlign: 'center', color: STUDY_THEME.muted }}>
            Loading your duels…
          </div>
        ) : (
          <>
            {/* Incoming */}
            <div style={sectionLabel} data-testid="section-incoming">
              📨 Incoming Invitations
              {inbox.incoming_count > 0 && (
                <span style={{
                  marginLeft: 8, padding: '2px 8px', borderRadius: 999,
                  background: STUDY_THEME.gold, color: STUDY_THEME.ink,
                  fontSize: 11, fontWeight: 700,
                }}>{inbox.incoming_count}</span>
              )}
            </div>
            {inbox.incoming.length === 0 ? (
              <div data-testid="empty-incoming" style={{
                ...cardStyle, textAlign: 'center', color: STUDY_THEME.muted,
                fontSize: 14, padding: 24,
              }}>
                No pending invitations. When a colleague challenges you,
                it&apos;ll show up here.
              </div>
            ) : (
              inbox.incoming.map((d) => (
                <div key={d.id} data-testid={`incoming-${d.id}`} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: '"Playfair Display", serif', fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
                        {d.deck_name || d.deck_id}
                      </div>
                      <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft }}>
                        <b>{d.opp_name || d.opp_email}</b> challenged you · {d.cards?.length || 0} cards
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => act(declineDuel, d.id)}
                        disabled={busy === d.id}
                        data-testid={`decline-${d.id}`}
                        style={pillBtn('white', '#DC2626', '#FCA5A5')}
                      >Decline</button>
                      <button
                        onClick={() => act(acceptDuel, d.id, `/study/duel?duel=${d.id}`)}
                        disabled={busy === d.id}
                        data-testid={`accept-${d.id}`}
                        style={pillBtn(STUDY_THEME.gold, STUDY_THEME.ink, STUDY_THEME.goldDark)}
                      >Accept & Play →</button>
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Active */}
            <div style={sectionLabel} data-testid="section-active">🔥 Active Duels</div>
            {inbox.active.length === 0 ? (
              <div data-testid="empty-active" style={{
                ...cardStyle, textAlign: 'center', color: STUDY_THEME.muted,
                fontSize: 14, padding: 24,
              }}>
                No duels in progress.
              </div>
            ) : (
              inbox.active.map((d) => (
                <div key={d.id} data-testid={`active-${d.id}`} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: '"Playfair Display", serif', fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
                        {d.deck_name || d.deck_id}
                      </div>
                      <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft, marginBottom: 6 }}>
                        vs <b>{d.opp_name || d.opp_email}</b> · {d.my_progress}/{d.cards?.length || 0} answered
                        {' · '}Partner: {d.opp_progress}/{d.cards?.length || 0}
                      </div>
                      <ScoreLine me={d.my_score} opp={d.opp_score} oppLabel={d.opp_name || 'Partner'} />
                    </div>
                    <button
                      onClick={() => navigate(`/study/duel?duel=${d.id}`)}
                      data-testid={`continue-${d.id}`}
                      style={pillBtn(STUDY_THEME.gold, STUDY_THEME.ink, STUDY_THEME.goldDark)}
                    >Continue →</button>
                  </div>
                </div>
              ))
            )}

            {/* Outgoing */}
            <div style={sectionLabel} data-testid="section-outgoing">📤 Waiting for Partner</div>
            {inbox.outgoing.length === 0 ? (
              <div data-testid="empty-outgoing" style={{
                ...cardStyle, textAlign: 'center', color: STUDY_THEME.muted,
                fontSize: 14, padding: 24,
              }}>
                No outgoing invitations.
              </div>
            ) : (
              inbox.outgoing.map((d) => (
                <div key={d.id} data-testid={`outgoing-${d.id}`} style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: '"Playfair Display", serif', fontSize: 20, fontWeight: 600, marginBottom: 4 }}>
                        {d.deck_name || d.deck_id}
                      </div>
                      <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft }}>
                        Waiting for <b>{d.opp_name || d.opp_email}</b> to accept
                      </div>
                    </div>
                    <button
                      onClick={() => act(cancelDuel, d.id)}
                      disabled={busy === d.id}
                      data-testid={`cancel-${d.id}`}
                      style={pillBtn('white', STUDY_THEME.inkSoft, STUDY_THEME.line)}
                    >Cancel</button>
                  </div>
                </div>
              ))
            )}

            {/* Complete */}
            <div style={sectionLabel} data-testid="section-complete">🏁 Recent Results</div>
            {inbox.complete.length === 0 ? (
              <div data-testid="empty-complete" style={{
                ...cardStyle, textAlign: 'center', color: STUDY_THEME.muted,
                fontSize: 14, padding: 24,
              }}>
                Nothing here yet. Finish a duel to see the head-to-head.
              </div>
            ) : (
              inbox.complete.slice(0, 20).map((d) => {
                const won = d.my_score > d.opp_score;
                const tie = d.my_score === d.opp_score;
                return (
                  <div key={d.id} data-testid={`complete-${d.id}`} style={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: '"Playfair Display", serif', fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
                          {d.deck_name || d.deck_id}
                        </div>
                        <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft, marginBottom: 6 }}>
                          vs <b>{d.opp_name || d.opp_email}</b>
                        </div>
                        <ScoreLine me={d.my_score} opp={d.opp_score} oppLabel={d.opp_name || 'Partner'} />
                      </div>
                      <div style={{
                        fontSize: 12, fontWeight: 700, letterSpacing: 1,
                        textTransform: 'uppercase',
                        color: won ? '#16A34A' : tie ? STUDY_THEME.muted : '#DC2626',
                      }}>
                        {won ? '🏆 Won' : tie ? '🤝 Tie' : '💥 Lost'}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
}
